import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useBasketDrawerActions } from '@common/components/BasketDrawer/hooks/useBasketDrawerActions';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import SectionContainer from '@common/components/SectionContainer';
import { DateCategoryPicker } from '@features/JourneyPicker';
import { useGetSpaClasses } from '@features/spa/hooks/useGetSpaClasses';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { useGetSpaClassesPage } from '@features/spa/hooks/useGetSpaClassesPage';
import { useTranslations } from '@lang';
import ClassListing from '@features/spa/pages/SpaClasses/components/ClassListing';
import { SpaClassesConfigurationWizard } from '@features/spa/pages/SpaClasses/components/SpaClassesConfigurationWizard';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { fetchCategories } from '@store/entity/actions';
import { entitySelectors } from '@store/entity/selectors';
import { signIn } from '@store/guest/actions';
import { guestSelectors } from '@store/guest/selectors';
import { changeJourneyParam } from '@store/yourJourney/actions';
import { ParsedJourneyParams, yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import { mapToObject } from '@utils/objects/mapToObject';
import { some } from '@utils/some';
import Cookie from 'js-cookie';
import moment, { Moment } from 'moment';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ThunkActionDispatch } from 'redux-thunk';
import { pipe } from 'remeda';
import { BasketDetails, Guest } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { SelectedTimeSlotPartial, SpaDateToClassTimeSlots } from './SpaClasses.types';
import { groupByTimeslotStartDateTime } from './helpers/spaClasses.helper';
import { useGetSpaSettings } from '@features/spa';

type CreateBasketFunction = ThunkActionDispatch<typeof createBasket>;
type UpdateBasketFunction = ThunkActionDispatch<typeof updateBasket>;

type SpaClassesComponentProps = {
  basketGuests: Guest[];
  basketDetails: BasketDetails;
  changeJourneyParam: (...args: any) => void;
  createBasket: CreateBasketFunction;
  isBasketFetching: boolean;
  journeyParams: ParsedJourneyParams;
  language: string;
  property: Property;
  showGenderNeutral: boolean;
  signIn: (...args: any[]) => any;
  spaClassesCategories: any[];
  updateBasket: UpdateBasketFunction;
  userHasAccessToSpaClasses: boolean;
};

export const SpaClasses = ({
  basketDetails,
  basketGuests,
  changeJourneyParam,
  createBasket,
  isBasketFetching,
  journeyParams,
  language,
  property,
  signIn,
  showGenderNeutral,
  spaClassesCategories,
  updateBasket,
  userHasAccessToSpaClasses,
}: SpaClassesComponentProps) => {
  const { t } = useTranslations();
  const [selectedDate, setSelectedDate] = useState<Moment | string>(
    journeyParams.fromDate || moment()
  );
  const [date, setDate] = useState<Moment | null>();
  const [isConfigurationOpen, setIsConfigurationOpen] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [selectedTimeslot, setSelectedTimeslot] = useState<SelectedTimeSlotPartial>({
    startDateTime: null,
    endDateTime: null,
    classId: null,
  });
  const [selectedCategory, setSelectedCategory] = useState({
    label: t('common.allCategories'),
    value: null,
  });

  const { data: classes, isLoading: isSpaClassesFetching } = useGetSpaClasses(
    property.id,
    language,
    parseDateToString(selectedDate)
  );

  const { data: spaClassesPage } = useGetSpaClassesPage(property.id, language);

  const hasUncategorizedClasses = (classes || []).some(
    (spaClass) => !spaClass.categories || spaClass.categories.length === 0
  );

  const categoryOptions = [
    { label: t('common.allCategories'), value: null },
    ...spaClassesCategories.map((category) => ({
      label: category.name,
      value: category.id,
    })),
    ...(hasUncategorizedClasses
      ? [{ label: t('spa.noCategory'), value: UNCATEGORIZED_VALUE }]
      : []),
  ];

  const timeslots = classes
    ?.filter((c) => some(c.availability))
    .flatMap((c) => {
      const res = c.availability.map((a) => ({
        classId: c.id,
        ...a,
      }));
      return res;
    });

  const spaClassesTimeSlots =
    timeslots && classes && groupByTimeslotStartDateTime(timeslots, classes);

  const showBasketDrawer = isConfirmationVisible;
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const openConfiguration = (timeslot: SelectedTimeSlotPartial) => {
    setIsConfigurationOpen(true);
    setSelectedTimeslot(timeslot);
  };

  const hideConfirmation = () => {
    setIsConfirmationVisible(false);
  };

  const showConfirmation = () => {
    setIsConfirmationVisible(true);
  };

  const closeConfiguration = () => {
    setIsConfigurationOpen(false);
    setSelectedTimeslot({
      startDateTime: null,
      endDateTime: null,
      classId: null,
    });
  };

  const handleTabChange = (selectedDate: string) => {
    setSelectedDate(selectedDate);
  };

  const handleUpdate = (date: Moment | null | undefined) => {
    if (date) {
      changeJourneyParam('fromDate', date);
      changeJourneyParam('spaDate', date);
      setSelectedDate(date);
    }
  };

  const handleCategoryChange = (selectedCategory) => {
    setSelectedCategory(selectedCategory);
  };

  const handleBasketUpdate = async ({ guests }) => {
    const { startDateTime, endDateTime, classId } = selectedTimeslot;

    const basketId = Cookie.get('basketId');

    const parsedClassItem = {
      quantity: 1,
      type: 'spa-classes',
      checkIn: moment(startDateTime).format(),
      checkOut: moment(endDateTime).format(),
      guestDetails: guests,
      bookable: {
        ids: {
          id: classId,
          type: 'spa-classes',
        },
      },
    };

    if (basketId) {
      return updateBasket(property.id, basketId, language, {
        items: [...basketDetails.items, parsedClassItem],
      }).then(() => showConfirmation());
    }
    return createBasket(property.id, language, {
      items: [parsedClassItem],
    }).then(() => showConfirmation());
  };

  const filterTimeSlotsBySelectedCategory = (spaDateToClassTimeSlots: SpaDateToClassTimeSlots) => {
    const selectedCategoryValue = selectedCategory ? selectedCategory.value : null;

    const filteredTimeTuple = Object.entries(spaDateToClassTimeSlots);

    if (selectedCategoryValue === UNCATEGORIZED_VALUE) {
      return mapToObject(filteredTimeTuple, ([date, spaClasses]) => {
        const filteredClasses = spaClasses.filter(
          ({ spaClass }) => !spaClass.categories || spaClass.categories.length === 0
        );

        if (some(filteredClasses)) {
          return { [date]: filteredClasses };
        }

        return {};
      });
    }

    if (selectedCategoryValue) {
      return mapToObject(filteredTimeTuple, ([date, spaClasses]) => {
        const filteredClasses = spaClasses.filter(({ spaClass }) =>
          spaClass.categories?.find(({ id }) => id === selectedCategoryValue)
        );

        if (some(filteredClasses)) {
          return { [date]: filteredClasses };
        }

        return {};
      });
    }
    return spaDateToClassTimeSlots;
  };

  const filterFutureTimeslotsBySelectedDate = (
    spaDateToClassTimeSlots: SpaDateToClassTimeSlots
  ) => {
    const filteredTimeslots = Object.entries(spaDateToClassTimeSlots).filter(
      ([date, _]) => moment(selectedDate).isSame(date, 'day') && moment(date).isAfter(moment())
    );

    return mapToObject(filteredTimeslots, ([date, spaClasses]) => ({
      [date]: spaClasses,
    }));
  };

  const sortTimeslotsInDateOrderAscending = (spaDateToClassTimeSlots: SpaDateToClassTimeSlots) => {
    const sortedTimeslotsTuple = Object.entries(spaDateToClassTimeSlots).sort(
      ([a], [b]) => new Date(a).getTime() - new Date(b).getTime()
    );

    return mapToObject(sortedTimeslotsTuple, ([date, spaClasses]) => ({
      [date]: spaClasses,
    }));
  };

  const sortedAndFilteredTimeslots = useMemo(() => {
    if (!spaClassesTimeSlots) return;
    return pipe(
      spaClassesTimeSlots,
      filterFutureTimeslotsBySelectedDate,
      filterTimeSlotsBySelectedCategory,
      sortTimeslotsInDateOrderAscending
    );
  }, [spaClassesTimeSlots]);

  const weekDays = Array.from({ length: 8 }, (_, index) =>
    moment(journeyParams.fromDate).add(index, 'days')
  );

  const { title = '', content = '' } = spaClassesPage || {};

  const spaClassesModules = property?.modulesExcludingDisabled?.spa?.features?.classes;
  const classesVisibleToMembers = spaClassesModules?.features?.visibleToMembers?.enabled;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const checkoutCta = useBasketDrawerActions();

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
  });

  return (
    <div>
      <SectionContainer>
        <DateCategoryPicker
          fromDate={date || moment(journeyParams.fromDate)}
          onDateChange={(date) => setDate(date)}
          categories={categoryOptions}
          onCategoryChange={handleCategoryChange}
          selectedCategory={selectedCategory}
          handleUpdate={() => handleUpdate(date)}
        />
      </SectionContainer>
      {userHasAccessToSpaClasses ? (
        <>
          <ClassListing
            weekDays={weekDays}
            spaClasses={sortedAndFilteredTimeslots}
            onTabChange={handleTabChange}
            isSpaClassesFetching={isSpaClassesFetching}
            selectedDate={selectedDate || journeyParams.fromDate}
            handleBookClass={openConfiguration}
            title={title}
            content={content}
            spaSettings={spaSettings}
          />
          {isConfigurationOpen && (
            <SpaClassesConfigurationWizard
              selectedTimeslot={selectedTimeslot as any}
              closeConfiguration={closeConfiguration}
              handleBasketUpdate={handleBasketUpdate}
              isFetching={isBasketFetching}
              existingGuests={basketGuests}
              property={property}
              signIn={signIn}
              showGenderNeutral={showGenderNeutral}
            />
          )}
          {showBasketDrawer && (
            <BasketDrawer
              currencyCode={basketDetails.total.currencyCode}
              open={isConfirmationVisible}
              onClose={hideConfirmation}
              numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
              totalCost={basketDetails.total.amount}
              title={t('basketDrawer.title')}
              items={basketDetails?.items}
              modules={modulesToRecommend}
              hideArrivalTimes={hideArrivalTimes}
              {...checkoutCta}
            />
          )}
        </>
      ) : (
        <SectionContainer>
          {t(classesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn', {
            spaEntity: 'spa classes',
          })}
        </SectionContainer>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  property: entitySelectors.getProperty(state),
  spaClassesCategories: entitySelectors.getSpaClassesCategories(state),
  journeyParams: yourJourneySelectors.getParsedJourneyParams(state),
  basketDetails: basketSelectors.getBasketDetails(state),
  isBasketFetching: basketSelectors.getIsBasketDetailsFetching(state),
  basketGuests: basketSelectors.getBasketGuests(state),
  guest: guestSelectors.getGuest(state),
  showGenderNeutral: entitySelectors.isSpaShowGenderNeutralEnabled(state),
  userHasAccessToSpaClasses: guestSelectors.userHasAccessToSpaEntity(state, 'classes'),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createBasket,
      updateBasket,
      fetchCategories,
      changeJourneyParam,
      signIn,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SpaClasses as any) as any;
